import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ConfirmDialog, RichTextEditor, Status } from '@/components';
import { Model } from '@/types/common';
import {
  ConditionField,
  PackageDetail,
  PackageFormValues,
  PackageHeldBy,
  PackageStatus,
  PackageVisibility,
  RentType,
} from '@/types/package';
import { getListModel } from '@/services/api/models';
import { getListProvider, Provider } from '@/services/api/providers';
import {
  archivePackage,
  createPackage,
  getPackageById,
  PackageInUseError,
  unarchivePackage,
  updatePackage,
} from '@/services/api/packages';
import { extractApiErrorMessage, formatRupiah } from '@/utils';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  changedHeldPriceFields,
  compileCriteria,
  fromPackageDetail,
  INITIAL_PACKAGE_FORM,
  PackageFormErrors,
  toPackageWriteBody,
  validatePackageForm,
} from './packageForm';
import {
  campaignUrl,
  formatHolderStanding,
  liveHolders,
  toHeldByCell,
} from './packageDisplay';
import { PackageDetailSection } from './components/PackageDetailSection';
import { PackageVehiclesModal } from './components/PackageVehiclesModal';
import PackageDepositSection from './components/PackageDepositSection';
import PackageHoldersNotice from './components/PackageHoldersNotice';
import PackagePreviewCard from './components/PackagePreviewCard';
import PackageVisibilitySection from './components/PackageVisibilitySection';
import { FormCard } from './components/PackageFormControls';
import { packageStatusAction, usePackageStatusDialog } from './packageStatus';

/**
 * Clients are fleet providers, and the list is open-ended: only the first page
 * is fetched, and anything past it is reached by a server-side search on name
 * rather than by downloading the lot and filtering in the browser.
 */
const CLIENT_PAGE_SIZE = 100;
const CLIENT_SEARCH_DEBOUNCE_MS = 300;

/** The grey icon list under a dialog's description. */
const DialogNotes: React.FC<{
  items: { icon: string; text: React.ReactNode }[];
}> = ({ items }) => (
  <ul className="mt-4 space-y-2 rounded-lg bg-[#F9FAFB] p-3 text-[13px] leading-5 text-gray-700">
    {items.map((item) => (
      <li key={item.icon} className="flex items-start gap-2">
        <i
          className={`${item.icon} text-[16px] leading-5 text-gray-500`}
          aria-hidden="true"
        />
        <span>{item.text}</span>
      </li>
    ))}
  </ul>
);

const CreatePackagePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [values, setValues] = useState<PackageFormValues>(INITIAL_PACKAGE_FORM);
  const [errors, setErrors] = useState<PackageFormErrors>({});
  const [models, setModels] = useState<Model[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const [clients, setClients] = useState<Provider[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  /**
   * Every client seen so far, by id. A search replaces the visible page, so
   * without this the chip for an already-selected client would fall back to
   * showing its raw id the moment it drops out of the current results. State
   * rather than a ref, so the options memo below actually recomputes when a
   * name is learned.
   */
  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [isLoadingPackage, setIsLoadingPackage] = useState(!!id);
  const [isSaving, setIsSaving] = useState(false);
  // Saving mid-upload would post a temp URL the upload has not finished
  // writing, and the API's relocate would 400 on a missing object.
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiveOpen, setIsUnarchiveOpen] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  // Holders from a 409 PACKAGE_IN_USE archive refusal (F4); null = none.
  const [inUseHolders, setInUseHolders] = useState<PackageHeldBy[] | null>(
    null
  );
  const [isPublicConfirmOpen, setIsPublicConfirmOpen] = useState(false);
  // Kept so PUT can preserve the §4.1 fields the form does not collect.
  const [existing, setExisting] = useState<PackageDetail | null>(null);
  const [isVehiclesOpen, setIsVehiclesOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadModels = async () => {
      setIsLoadingModels(true);
      try {
        const response = await getListModel({ page: '1', size: '1000' });
        if (cancelled) return;
        setModels(response.data || []);
      } catch (error) {
        if (cancelled) return;
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load models')
        );
        setModels([]);
      } finally {
        if (!cancelled) setIsLoadingModels(false);
      }
    };

    loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  // Only packages that actually constrain by client need the list, which is
  // most of them not at all — so the fetch waits for such a row to exist.
  const hasClientCondition = values.conditions.some(
    (condition) => condition.field === ConditionField.CLIENT
  );

  useEffect(() => {
    if (!hasClientCondition) return;

    let cancelled = false;
    const search = clientSearch.trim();
    setIsLoadingClients(true);

    const timer = setTimeout(
      () => {
        getListProvider({
          page: '1',
          size: String(CLIENT_PAGE_SIZE),
          ...(search ? { search } : {}),
        })
          .then((response) => {
            if (cancelled) return;
            const page = response.data || [];
            setClients(page);
            setClientNames((known) => {
              const learned = { ...known };
              page.forEach((client) => {
                learned[String(client.id)] = client.name;
              });
              return learned;
            });
          })
          .catch((error) => {
            if (cancelled) return;
            showErrorToast(
              'Error',
              extractApiErrorMessage(error, 'Failed to load clients')
            );
            setClients([]);
          })
          .finally(() => {
            if (!cancelled) setIsLoadingClients(false);
          });
      },
      // The debounce is there to collapse keystrokes; the first, unsearched
      // load has nothing to wait for.
      search ? CLIENT_SEARCH_DEBOUNCE_MS : 0
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clientSearch, hasClientCondition]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const loadPackage = async () => {
      setIsLoadingPackage(true);
      try {
        const detail = await getPackageById(id);
        if (cancelled) return;
        setExisting(detail);
        setValues(fromPackageDetail(detail));
      } catch (error) {
        if (cancelled) return;
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load package')
        );
        navigate('/packages');
      } finally {
        if (!cancelled) setIsLoadingPackage(false);
      }
    };

    loadPackage();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  /**
   * "Alva - N3", not "N3": several OEMs ship models under the same name
   * (three separate "Tempur" rows today), so the bare name cannot be picked
   * from with any confidence. Sorted by the same label so one OEM's models
   * sit together instead of scattered through the list.
   */
  const modelOptions = useMemo(
    () =>
      models
        .map((model) => ({
          value: model.ID,
          label: model.oemName ? `${model.oemName} - ${model.name}` : model.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'id')),
    [models]
  );

  /**
   * Which clients the package currently selects, as one string — the options
   * below only have to be rebuilt when this changes, not on every keystroke
   * in an unrelated condition row.
   */
  const selectedClientIDs = useMemo(
    () =>
      values.conditions
        .filter((condition) => condition.field === ConditionField.CLIENT)
        .flatMap((condition) => condition.values)
        .join(','),
    [values.conditions]
  );

  /**
   * The current page, with any already-selected client appended so a selection
   * never disappears from the list it was made in. Provider ids are numeric;
   * the condition rows carry them as strings.
   */
  const clientOptions = useMemo(() => {
    const options = clients.map((client) => ({
      value: String(client.id),
      label: client.name,
    }));
    const listed = new Set(options.map((option) => option.value));

    selectedClientIDs
      .split(',')
      .filter(Boolean)
      .forEach((clientID) => {
        if (listed.has(clientID)) return;
        listed.add(clientID);
        options.push({
          value: clientID,
          // A client selected on an earlier page, or stored on the package,
          // that this search did not return: name it if it has ever been
          // fetched, otherwise show the id rather than an empty chip.
          label: clientNames[clientID] ?? `Client #${clientID}`,
        });
      });

    return options;
  }, [clients, clientNames, selectedClientIDs]);

  // model.contractType is RTO *eligibility*: RTO models may be sold either
  // way, RENT models are rent-only (TRD P7).
  const rtoEligible = useMemo(() => {
    if (!values.modelID) return undefined;
    const selected = models.find((model) => model.ID === values.modelID);
    if (!selected) return undefined;
    return selected.contractType === RentType.RTO;
  }, [models, values.modelID]);

  const handleChange = useCallback(
    <K extends keyof PackageFormValues>(
      field: K,
      value: PackageFormValues[K]
    ) => {
      setValues((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const isArchived = existing?.status === PackageStatus.ARCHIVED;

  const handleArchive = async () => {
    if (!id) return;
    setIsArchiving(true);
    try {
      await archivePackage(id);
      showSuccessToast('Success', 'Package diarsipkan');
      navigate('/packages');
    } catch (error) {
      if (error instanceof PackageInUseError) {
        setInUseHolders(error.heldBy);
        return;
      }
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to archive package')
      );
    } finally {
      setIsArchiving(false);
      setIsArchiveOpen(false);
    }
  };

  const rehydrate = (detail: PackageDetail) => {
    setExisting(detail);
    setValues(fromPackageDetail(detail));
  };

  // Stays on the page: the package comes back INACTIVE, and the next step is
  // reviewing it here and switching it on.
  const handleUnarchive = async () => {
    if (!id) return;
    setIsUnarchiving(true);
    try {
      await unarchivePackage(id);
      rehydrate(await getPackageById(id));
      showSuccessToast('Success', 'Package dikembalikan sebagai Inactive');
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to unarchive package')
      );
    } finally {
      setIsUnarchiving(false);
      setIsUnarchiveOpen(false);
    }
  };

  // Unknown (`heldBy: null`) counts as held: an unchecked reach is not free.
  const heldByCell = existing
    ? toHeldByCell(existing.heldBy, existing.visibility)
    : null;
  const isHeldKhusus =
    heldByCell?.kind === 'holders' || heldByCell?.kind === 'unknown';

  const refusedHolders = inUseHolders ?? [];
  const refusedLink = (() => {
    const holder = liveHolders(refusedHolders)[0] ?? refusedHolders[0];
    return holder ? campaignUrl(holder.campaignId) : null;
  })();
  const refusedNames = refusedHolders
    .map((holder) => `${holder.campaignName} (${formatHolderStanding(holder)})`)
    .join(', ');
  const closeRefusal = () => setInUseHolders(null);
  const referralPrice = formatRupiah(Number(values.dailyPrice), '-', {
    space: false,
  });

  const statusAction = existing && packageStatusAction(existing.status);
  const statusDialog = usePackageStatusDialog(rehydrate);
  // The status flip writes the stored package, so unsaved edits would be
  // silently dropped from the form; they have to be saved or reverted first.
  // Compared as PUT bodies: the form's row keys are fresh uuids on every
  // hydrate and would always differ.
  const savedBody = useMemo(
    () =>
      existing &&
      JSON.stringify(toPackageWriteBody(fromPackageDetail(existing), existing)),
    [existing]
  );
  const isDirty =
    !!existing &&
    JSON.stringify(toPackageWriteBody(values, existing)) !== savedBody;

  const changedPriceFields = existing
    ? changedHeldPriceFields(values, existing)
    : [];

  const submit = async () => {
    setIsSaving(true);
    try {
      const body = toPackageWriteBody(values, existing);
      if (isEditMode && id) {
        await updatePackage(id, body);
        showSuccessToast('Success', 'Package updated successfully');
      } else {
        await createPackage(body);
        showSuccessToast('Success', 'Package created successfully');
      }
      navigate('/packages');
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(
          error,
          `Failed to ${isEditMode ? 'update' : 'create'} package`
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => {
    const nextErrors = validatePackageForm(values, { rtoEligible });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      showErrorToast('Error', 'Periksa kembali data package');
      return;
    }

    // F5: confirm only when held or unknown ("saat package dipakai"); a free
    // Khusus going Publik reaches no referee. Fleet does not refuse it.
    if (isHeldKhusus && values.visibility === PackageVisibility.PUBLIC) {
      setIsPublicConfirmOpen(true);
      return;
    }
    submit();
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-200 shrink-0">
        <button
          type="button"
          onClick={() => navigate('/packages')}
          aria-label="Tutup"
          className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700"
        >
          <i className="ri-close-line text-[20px]"></i>
        </button>
        <span className="w-px h-6 bg-gray-200" />
        <h1 className="text-[18px] font-medium text-gray-900">
          {isEditMode ? 'Edit Package' : 'Create Package'}
        </h1>
        {existing && (
          <Status value={existing.status} type="packageStatus" size="medium" />
        )}

        {/* Edit only: an archived package offers Unarchive in place of
            Archive, and Archive waits for the detail response to land. */}
        {isEditMode && (
          <div className="ml-auto flex items-center gap-1">
            {isArchived ? (
              <button
                type="button"
                onClick={() => setIsUnarchiveOpen(true)}
                disabled={isUnarchiving}
                className="px-3 py-2 text-[14px] font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-300 disabled:cursor-not-allowed"
              >
                <i className="ri-inbox-unarchive-line text-[14px] mr-1"></i>
                Unarchive Package
              </button>
            ) : (
              <>
                {existing && statusAction && (
                  <>
                    {isDirty && (
                      <span className="text-[12px] text-gray-500">
                        Simpan perubahan dulu untuk{' '}
                        {statusAction.label.toLowerCase()}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        id && statusDialog.request(id, existing.status)
                      }
                      disabled={isDirty || statusDialog.isBusy || isSaving}
                      className={`px-3 py-2 text-[14px] font-medium disabled:text-gray-300 disabled:cursor-not-allowed ${
                        statusAction.next === PackageStatus.ACTIVE
                          ? 'text-primary-600 hover:text-primary-700'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <i className={`${statusAction.icon} text-[14px] mr-1`}></i>
                      {statusAction.label}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setIsArchiveOpen(true)}
                  disabled={!existing || isArchiving || isSaving}
                  title={existing ? undefined : 'Memuat data package...'}
                  className="px-3 py-2 text-[14px] font-medium text-red-500 hover:text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                  <i className="ri-archive-line text-[14px] mr-1"></i>
                  Archive Package
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col lg:flex-row items-start">
        <div className="w-full lg:flex-1 px-4 py-4 space-y-4">
          <PackageVisibilitySection
            value={values.visibility}
            onChange={(value) => handleChange('visibility', value)}
            action={
              existing && (
                <Status
                  value={existing.visibility}
                  type="packageVisibility"
                  size="small"
                />
              )
            }
          >
            {existing?.visibility === PackageVisibility.PRIVATE && (
              <PackageHoldersNotice
                visibility={existing.visibility}
                heldBy={existing.heldBy}
                changedFields={changedPriceFields}
              />
            )}
          </PackageVisibilitySection>

          <PackageDetailSection
            values={values}
            errors={errors}
            onChange={handleChange}
            modelOptions={modelOptions}
            isLoadingModels={isLoadingModels}
            clientOptions={clientOptions}
            isLoadingClients={isLoadingClients}
            onClientSearch={setClientSearch}
            onUploadingChange={setIsUploadingImage}
            onViewUnits={
              values.modelID ? () => setIsVehiclesOpen(true) : undefined
            }
          />

          <PackageDepositSection
            values={values}
            errors={errors}
            onChange={handleChange}
          />

          <FormCard title="Benefit">
            <div className="px-5 py-4">
              <RichTextEditor
                value={values.benefit}
                onChange={(html) => handleChange('benefit', html)}
                placeholder="Tuliskan benefit package ini."
              />
            </div>
          </FormCard>

          <FormCard title="Syarat & Ketentuan">
            <div className="px-5 py-4">
              <RichTextEditor
                value={values.termsAndConditions}
                onChange={(html) => handleChange('termsAndConditions', html)}
                placeholder="Tuliskan syarat & ketentuan package ini."
              />
            </div>
          </FormCard>
        </div>

        <div className="w-full lg:w-[580px] shrink-0 bg-[#F7F7F7] self-stretch px-6 py-4">
          <div className="lg:sticky lg:top-4">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-[16px] font-medium text-gray-900">
                  Pratinjau
                </h2>
                <p className="text-[13px] text-gray-500 mt-0.5">
                  Ini adalah bagaimana package akan terlihat.
                </p>
              </div>
              <Button
                variant="primary"
                size="medium"
                onClick={handleSave}
                // The API refuses to edit an archived package; Unarchive first.
                disabled={
                  isSaving || isLoadingPackage || isUploadingImage || isArchived
                }
              >
                {isUploadingImage
                  ? 'Mengunggah gambar...'
                  : isSaving
                    ? 'Menyimpan...'
                    : isEditMode
                      ? 'Simpan Perubahan'
                      : 'Simpan Package'}
              </Button>
            </div>

            <PackagePreviewCard values={values} />
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={isUnarchiveOpen}
        title="Kembalikan package dari arsip?"
        description="Package kembali sebagai Inactive: belum dicocokkan ke unit dan belum muncul di aplikasi mitra. Cek harga dan aturannya, lalu klik Aktifkan di atas."
        confirmLabel="Kembalikan"
        cancelLabel="Batal"
        onConfirm={handleUnarchive}
        onCancel={() => setIsUnarchiveOpen(false)}
        isBusy={isUnarchiving}
      />

      {statusDialog.dialog}

      <ConfirmDialog
        isOpen={isArchiveOpen}
        title="Arsipkan package ini?"
        description="Package berhenti dicocokkan ke unit baru dan tidak lagi muncul di aplikasi mitra. Sewa yang sedang berjalan tidak terpengaruh karena memakai snapshot ketentuannya sendiri."
        confirmLabel={isArchiving ? 'Mengarsipkan...' : 'Arsipkan'}
        cancelLabel="Batal"
        confirmVariant="danger"
        onConfirm={handleArchive}
        onCancel={() => setIsArchiveOpen(false)}
        isBusy={isArchiving}
      />

      {/* F4: the link is hidden when REACT_APP_BASECAMP_URL is unset. */}
      <ConfirmDialog
        isOpen={inUseHolders !== null}
        title="Package ini belum bisa diarsipkan"
        description={`${existing?.name ?? 'Package ini'} sedang dipakai campaign referral${
          refusedNames ? ` ${refusedNames}` : ''
        }.`}
        cancelLabel={refusedLink ? 'Tutup' : undefined}
        confirmLabel={refusedLink ? 'Buka campaign' : 'Tutup'}
        confirmIcon={refusedLink ? 'ri-arrow-right-up-line' : undefined}
        onConfirm={() => {
          if (refusedLink) {
            window.open(refusedLink, '_blank', 'noopener,noreferrer');
          }
          closeRefusal();
        }}
        onCancel={closeRefusal}
      >
        <DialogNotes
          items={[
            {
              icon: 'ri-pause-circle-line',
              text: 'Akhiri atau arsipkan campaign di On Demand System dulu. Menjeda campaign tidak melepas package.',
            },
            {
              icon: 'ri-time-line',
              text: 'Setelah campaign selesai, package otomatis bebas dan bisa diarsipkan atau dipakai campaign lain.',
            },
          ]}
        />
      </ConfirmDialog>

      {/* F5 */}
      <ConfirmDialog
        isOpen={isPublicConfirmOpen}
        title="Jadikan package ini Publik?"
        description={`${
          values.name.trim() || 'Package ini'
        } akan tampil di katalog sewa untuk semua mitra yang memenuhi condition, bukan hanya pemakai kode referral.`}
        confirmLabel={isSaving ? 'Menyimpan...' : 'Jadikan Publik'}
        confirmIcon="ri-eye-line"
        cancelLabel="Batal"
        onConfirm={async () => {
          await submit();
          setIsPublicConfirmOpen(false);
        }}
        onCancel={() => setIsPublicConfirmOpen(false)}
        isBusy={isSaving}
      >
        <DialogNotes
          items={[
            {
              icon: 'ri-megaphone-line',
              text:
                heldByCell?.kind === 'holders'
                  ? `Campaign ${heldByCell.holders
                      .map((holder) => holder.campaignName)
                      .join(', ')} tetap memakai package ini sampai selesai.`
                  : 'Pemakaian oleh campaign tidak bisa dicek saat ini; campaign yang memakainya tetap memakai package ini sampai selesai.',
            },
            {
              icon: 'ri-price-tag-3-line',
              text: `Harga referral ${referralPrice}/hari jadi terlihat oleh semua mitra di katalog.`,
            },
          ]}
        />
      </ConfirmDialog>

      {/* The form's own criteria, saved or not, so the list always matches
          the count the link sits in. */}
      {isVehiclesOpen && values.modelID && (
        <PackageVehiclesModal
          onClose={() => setIsVehiclesOpen(false)}
          source={{
            modelID: values.modelID,
            criteria: compileCriteria(values),
          }}
          packageName={values.name || existing?.name}
        />
      )}
    </div>
  );
};

export default CreatePackagePage;
